import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import type { MotivoAbertura } from "./abrir";

// O IO das Calculadoras da Reforma (000221) — zero regra de tributo.
//
// Nada aqui calcula imposto, e nada aqui deve passar a calcular. Quem responde
// "quanto é o IBS desta operação" é a Receita Federal, pela API do piloto CBS;
// este módulo transporta o pedido, transporta a resposta e traduz falha em
// frase. Uma conta escrita aqui seria a segunda definição do mesmo número, que
// divergiria da oficial no primeiro ajuste de alíquota — e a ferramenta existe
// justamente para dar ao aluno o número da fonte.
//
// ---------------------------------------------------------------------------
// Por que tudo passa pela Edge Function
// ---------------------------------------------------------------------------
// Medido em 14/09: o preflight `OPTIONS` da API do gov responde 403 e a resposta
// simples não traz `access-control-allow-origin`. O navegador não alcança
// aquela API por nenhum dos dois caminhos — inclusive as consultas de
// referência (UFs, municípios, CSTs). Por isso até a lista de estados vem por
// `calcular-tributos`.

/** O card, e portanto o portão. */
export const CARD = "calculadoras";

/** As três calculadoras da tela. */
export type Calculadora = "regime-geral" | "simplificado" | "base-calculo";

/** As rotas da API do gov que a function conhece. */
export type Rota =
  | "ufs"
  | "municipios"
  | "dfe"
  | "csts"
  | "csts-is"
  | "ncm"
  | "nbs"
  | "nbs-lista"
  | "aliquota-uniao"
  | "aliquota-uf"
  | "aliquota-municipio"
  | "split-cadastrados"
  | "classificacoes-cbs-ibs"
  | "classificacoes-is"
  | "regime-geral"
  | "split-payment"
  | "base-cbs-ibs"
  | "base-is";

/**
 * O desfecho de uma chamada, com as TRÊS situações separadas.
 *
 * A separação é a peça mais importante deste módulo, porque a conduta do aluno
 * muda em cada uma: em `recusa` ele corrige o formulário (e o texto do gov diz
 * o quê), em `indisponivel` ele tenta de novo mais tarde, e em `sem-permissao`
 * ele assina o contrato ou fala com o suporte. Uma mensagem genérica para as
 * três manda ao suporte inclusive quem tinha a solução na mão.
 */
export type Resposta<T> =
  | { situacao: "ok"; dados: T; simulado: number | null; aviso: string | null; guardado: boolean; calculoId: string | null; motivoDoRegistro: string | null }
  | { situacao: "recusa"; titulo: string; detalhe: string }
  | { situacao: "indisponivel"; motivo: string }
  | { situacao: "sem-permissao"; motivo: MotivoAbertura };

/** O que a tela manda quando quer que o cálculo entre no histórico. */
export type Guardar = { calculadora: Calculadora; rotulo?: string | null };

/**
 * Chama a Edge Function.
 *
 * O `card` diz QUAL ferramenta está pedindo — a function serve as Calculadoras
 * e o CST e ClassTrib, e o alcance de rotas de cada uma é uma lista fechada do
 * lado de lá. O padrão é `calculadoras` porque foi a primeira, e assim o código
 * que já estava no ar não precisou mudar.
 */
export async function chamar<T>(
  rota: Rota,
  opcoes: {
    params?: Record<string, string | number>;
    corpo?: unknown;
    guardar?: Guardar;
    card?: string;
  } = {},
): Promise<Resposta<T>> {
  try {
    const { data: sessao } = await supabase.auth.getSession();
    const token = sessao.session?.access_token;
    if (!token) return { situacao: "sem-permissao", motivo: "sem-sessao" };

    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(opcoes.params ?? {})) params[k] = String(v);

    const { data, error } = await supabase.functions.invoke("calcular-tributos", {
      body: {
        curso: cursoDaQuery(),
        card: opcoes.card ?? "calculadoras",
        rota,
        params,
        corpo: opcoes.corpo ?? null,
        guardar: opcoes.guardar ?? null,
      },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (error) {
      // O que importa está no CORPO da resposta, não em `error.message`: em
      // 4xx/5xx o supabase-js devolve `data: null` e guarda a resposta inteira
      // em `error.context`. Sem ler daí, "o NCM 2402 exige Imposto Seletivo" e
      // "o piloto está fora do ar" viram a mesma tela genérica.
      if (error instanceof FunctionsHttpError) {
        try {
          const corpo = await error.context.json();
          const status = error.context.status as number;
          if (status === 422) {
            return {
              situacao: "recusa",
              titulo: String(corpo?.titulo ?? "Dados não aceitos"),
              detalhe: String(corpo?.detalhe ?? "A calculadora da Receita recusou os dados enviados."),
            };
          }
          if (status === 403 || status === 409 || status === 401) {
            return { situacao: "sem-permissao", motivo: (corpo?.motivo ?? "falha") as MotivoAbertura };
          }
          return { situacao: "indisponivel", motivo: String(corpo?.motivo ?? "falha") };
        } catch {
          return { situacao: "indisponivel", motivo: "falha" };
        }
      }
      return { situacao: "indisponivel", motivo: "falha" };
    }

    const d = (data ?? {}) as {
      dados?: unknown;
      simulado?: number | null;
      aviso?: string | null;
      guardado?: boolean;
      calculo_id?: string;
      motivo?: string;
    };
    return {
      situacao: "ok",
      dados: d.dados as T,
      simulado: typeof d.simulado === "number" ? d.simulado : null,
      aviso: d.aviso ?? null,
      guardado: d.guardado === true,
      calculoId: d.calculo_id ?? null,
      motivoDoRegistro: d.motivo ?? null,
    };
  } catch {
    return { situacao: "indisponivel", motivo: "falha" };
  }
}

// ---------------------------------------------------------------------------
// O histórico
// ---------------------------------------------------------------------------
export type CalculoGuardado = {
  id: string;
  seq: number;
  calculadora: Calculadora;
  rota: Rota;
  rotulo: string | null;
  entrada: Record<string, unknown>;
  resultado: Record<string, unknown>;
  simulado: number | null;
  criado_em: string;
};

export type Historico = { total: number; teto: number; calculos: CalculoGuardado[] };

export async function meusCalculos(
  calculadora?: Calculadora,
): Promise<Historico | { motivo: MotivoAbertura } | null> {
  const { data, error } = await supabase.rpc("calc_meus_calculos", {
    p_curso: cursoDaQuery(),
    p_calculadora: calculadora ?? null,
    p_limite: 50,
  });
  if (error) return null;
  const r = (data ?? {}) as { ok?: boolean; motivo?: string } & Historico;
  if (!r.ok) return { motivo: (r.motivo ?? "falha") as MotivoAbertura };
  return {
    total: Number(r.total ?? 0),
    teto: Number(r.teto ?? 50),
    calculos: (r.calculos ?? []) as CalculoGuardado[],
  };
}

/** Apaga UM cálculo. `false` quer dizer "não apagou" — a tela avisa na linha. */
export async function apagarCalculo(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("calc_apagar", {
    p_curso: cursoDaQuery(),
    p_id: id,
  });
  if (error) return false;
  return ((data ?? {}) as { ok?: boolean }).ok === true;
}

/**
 * O curso pelo qual o card foi aberto. Vem do `?curso=` que `aberturaDoCard` põe
 * no link; hoje o card só pertence à Pós RT, então o padrão acerta sempre — e
 * cravar `pos-rt` sem a query seria descobrir a próxima pós pelo aluno recusado.
 */
export function cursoDaQuery(): string {
  const q = new URLSearchParams(window.location.search).get("curso");
  return q && q.trim() !== "" ? q : "pos-rt";
}
